import Image from 'next/image'
import Link from 'next/link'
import React from 'react'

import { Article } from '@/slices/blog/mocks/articles'
import { getAuthorById, getDefaultAuthor } from '@/slices/blog/mocks/authors'

import { CustomImage } from '@/shared/components/CustomImage/CustomImage'
import { pxToRem } from '@/shared/styles/themes'
import { Box, Card, CardContent, styled, Typography } from '@mui/material'

export interface ArticleCardProps {
  article: Article
}

export const ArticleCard: React.FC<ArticleCardProps> = ({ article }) => {
  const author = article.authorId
    ? getAuthorById(article.authorId) || getDefaultAuthor()
    : getDefaultAuthor()

  const formattedDate = new Date(article.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

  return (
    <Link href={`/blog/${article.slug}`} passHref legacyBehavior>
      <StyledCard>
        <ImageWrapper>
          <ImageInner>
            <CustomImage
              src={article.image}
              alt={article.title}
              variant="horizontal"
              objectFit="cover"
              borderRadius={0}
            />
          </ImageInner>
          {article.category && (
            <CategoryBadge>{article.category}</CategoryBadge>
          )}
        </ImageWrapper>

        <StyledCardContent>
          <ArticleTitle variant="h3">{article.title}</ArticleTitle>
          {article.excerpt && (
            <ArticleExcerpt variant="body2">{article.excerpt}</ArticleExcerpt>
          )}
          <MetaRow>
            <AuthorMeta>
              <AuthorAvatarImg
                src={author.avatar}
                alt={author.name}
                width={24}
                height={24}
              />
              <MetaText variant="caption">{author.name}</MetaText>
            </AuthorMeta>
            <MetaDot />
            <MetaText variant="caption">{formattedDate}</MetaText>
            {article.readTime && (
              <>
                <MetaDot />
                <MetaText variant="caption">{article.readTime}</MetaText>
              </>
            )}
          </MetaRow>
        </StyledCardContent>
      </StyledCard>
    </Link>
  )
}

const StyledCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  borderRadius: pxToRem(12),
  overflow: 'hidden',
  cursor: 'pointer',
  textDecoration: 'none',
  backgroundColor: theme.palette.common.white,
  border: 'none',
  boxShadow: 'none',
  transition: 'transform 0.35s ease',

  '&:hover': {
    transform: 'translateY(-2px)',
  },

  '&:hover .image-inner': {
    transform: 'scale(1.03)',
  },
}))

const ImageWrapper = styled(Box)({
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  borderRadius: pxToRem(12),
})

const ImageInner = styled(Box)({
  transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  className: 'image-inner',
})

ImageInner.defaultProps = {
  className: 'image-inner',
}

const CategoryBadge = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  bottom: pxToRem(12),
  left: pxToRem(12),
  fontSize: pxToRem(11),
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#fff',
  backgroundColor: 'rgba(19, 40, 72, 0.75)',
  backdropFilter: 'blur(8px)',
  padding: `${pxToRem(4)} ${pxToRem(10)}`,
  borderRadius: pxToRem(4),
  lineHeight: 1.4,

  [theme.breakpoints.down('sm')]: {
    fontSize: pxToRem(10),
    padding: `${pxToRem(3)} ${pxToRem(8)}`,
  },
}))

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: `${pxToRem(20)} ${pxToRem(4)}`,
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',

  '&:last-child': {
    paddingBottom: pxToRem(16),
  },

  [theme.breakpoints.down('sm')]: {
    padding: `${pxToRem(16)} ${pxToRem(2)}`,

    '&:last-child': {
      paddingBottom: pxToRem(12),
    },
  },
}))

const ArticleTitle = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(20),
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginBottom: pxToRem(10),
  lineHeight: 1.35,
  letterSpacing: '-0.01em',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',

  [theme.breakpoints.down('sm')]: {
    fontSize: pxToRem(17),
    marginBottom: pxToRem(8),
  },
}))

const ArticleExcerpt = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(14),
  fontWeight: 300,
  color: theme.palette.text.secondary,
  lineHeight: 1.65,
  marginBottom: pxToRem(16),
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flexGrow: 1,

  [theme.breakpoints.down('sm')]: {
    fontSize: pxToRem(13),
    marginBottom: pxToRem(12),
  },
}))

const MetaRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(8),
  marginTop: 'auto',
  flexWrap: 'wrap',
})

const AuthorMeta = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(6),
})

const AuthorAvatarImg = styled(Image)({
  borderRadius: '50%',
  objectFit: 'cover',
})

const MetaText = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(12),
  fontWeight: 400,
  color: theme.palette.text.disabled,
  lineHeight: 1,

  [theme.breakpoints.down('sm')]: {
    fontSize: pxToRem(11),
  },
}))

const MetaDot = styled('span')(({ theme }) => ({
  width: pxToRem(3),
  height: pxToRem(3),
  borderRadius: '50%',
  backgroundColor: theme.palette.text.disabled,
  flexShrink: 0,
}))
